import React, { useEffect, useState } from 'react';
import algoliasearch from 'algoliasearch/lite';
import {
  InstantSearch,
  Configure,
  useRange,
  useInstantSearch,
  useDynamicWidgets,
  useRefinementList,
} from 'react-instantsearch';
import * as Slider from '@radix-ui/react-slider';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  headingStyles,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
  standardTransition,
  visuallyHidden,
  zIndexLayers,
} from '../styles';
import { Button, Container, HtmlContent } from './ui';
import ProductCard from './ProductCard';
import { formatPrice } from '../utils';

const StyledProductListing = styled.section`
  ${sectionPaddings('25px', '40px')};
  background-color: ${standardColours.lightestGrey};
`;

const StyledHeader = styled.header`
  margin-bottom: 20px;
  text-align: center;

  ${minBreakpointQuery.small`
    margin-bottom: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 30px;
  `}
`;

const StyledHeading = styled.h1`
  ${headingStyles()};
`;

const StyledText = styled(HtmlContent)`
  margin-top: 10px;

  ${minBreakpointQuery.small`
    margin-top: 12px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 14px;
  `}

  p {
    margin-bottom: 10px;

    ${minBreakpointQuery.small`
      margin-bottom: 12px;
    `}

    ${minBreakpointQuery.large`
      margin-bottom: 14px;
    `}
  }
`;

const StyledInner = styled.div``;

const StyledRefineWrapper = styled.div`
  ${maxBreakpointQuery.smedium`
    margin-bottom: 15px;
  `}

  ${minBreakpointQuery.smedium`
    ${visuallyHidden()};
  `}
`;

const StyledRefine = styled(Button)`
  display: block;
  width: 100%;
  padding: 10px 16px;
  background-color: ${standardColours.white};
  color: ${standardColours.black};
  border: 1px solid ${standardColours.grey};
  font-weight: ${fontWeights.regular};

  &:hover {
    color: ${standardColours.white};
  }
`;

const StyledFilterItems = styled.div`
  ${({ showFilters }) => {
    if (showFilters) {
      return css`
        ${minBreakpointQuery.smedium`
          display: grid;
          grid-template-columns: 260px calc(100% - 280px);
          gap: 20px;
        `}

        ${minBreakpointQuery.large`
          grid-template-columns: 270px calc(100% - 300px);
          gap: 30px;
        `}

        ${minBreakpointQuery.xlarge`
          grid-template-columns: 275px calc(100% - 310px);
          gap: 35px;
        `}
      `;
    }
  }}
`;

const StyledFilters = styled.aside`
  background-color: ${standardColours.white};
  border: 1px solid ${standardColours.lightestGrey};
  border-radius: 15px;
  padding: 20px;

  ${maxBreakpointQuery.smedium`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: ${zIndexLayers.fourth};
    transform: translateX(-100%);
    transition: ${standardTransition('transform')};
    overflow-y: scroll;
  `}

  ${({ display }) => {
    if (display) {
      return css`
        ${maxBreakpointQuery.smedium`
          transform: translateX(0);
        `}
      `;
    }
  }}

  ${minBreakpointQuery.smedium`
    top: 10px;
    align-self: start;
  `}

  ${minBreakpointQuery.large`
    padding: 25px;
  `}

  ${minBreakpointQuery.xlarge`
    padding: 30px;
  `}
`;

const StyledFiltersHeader = styled.header`
  ${maxBreakpointQuery.smedium`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `}
`;

const StyledFiltersHeading = styled.h2`
  ${fontSize(17)}
  font-weight: ${fontWeights.semibold};
`;

const StyledClose = styled.button`
  ${maxBreakpointQuery.smedium`
    color: ${standardColours.black};
    ${fontSize(13)};
    background: none;
    border: none;
  `}

  ${minBreakpointQuery.smedium`
    ${visuallyHidden()};
  `}

  &:after {
    ${maxBreakpointQuery.smedium`
      content: '✕';
      position: relative;
      bottom: 1px;
      margin-left: 12px;
      font-weight: ${fontWeights.bold};
      ${fontSize(22)};
      vertical-align: middle;
    `}
  }
`;

const StyledFilter = styled.fieldset`
  margin: 25px 0 0;
  padding: 0;
  border: none;
  border-bottom: 1px solid ${standardColours.grey};
  padding-bottom: 20px;
  ${fontSize(14)};

  ${({ display }) => {
    if (!display) {
      return css`
        display: none;
      `;
    } else {
      return css`
        display: block;
      `;
    }
  }}

  ${({ lastDisplayed }) => {
    if (lastDisplayed) {
      return css`
        border-bottom: none;
      `;
    }
  }}
`;

const StyledFilterHeading = styled.legend`
  position: relative;
  ${fontSize(16)}
  font-weight: ${fontWeights.semibold};
  padding: 0;
  width: 100%;
  cursor: pointer;

  &:after {
    content: ${({ display }) => (display ? "'-'" : "'+'")};
    position: absolute;
    top: 0;
    right: 0;
    color: ${standardColours.black};
    font-weight: ${fontWeights.regular};
    ${fontSize(25)};
    line-height: 1;
    transform: translateY(-25%);
  }
`;

const StyledFilterList = styled.div`
  ${({ isPrice }) => {
    if (!isPrice) {
      return css`
        padding-top: 12px;
      `;
    }
  }}

  ${({ display }) => {
    if (display) {
      return css`
        display: none;
      `;
    } else {
      return css`
        display: block;
      `;
    }
  }}
`;

const StyledFilterListItems = styled.ul`
  ${minBreakpointQuery.smedium`
    max-height: 250px;
    overflow-y: scroll;
    padding-bottom: 12px;
  `}
`;

const StyledFilterListItem = styled.li`
  margin-top: 12px;

  &:first-child {
    margin-top: 0;
  }
`;

// moved up for checked styling
const StyledCheckboxText = styled.span`
  position: relative;
  padding-left: 30px;
  padding-right: 5px;
  color: ${standardColours.black};
  ${fontSize(13)};
  line-height: 1.5;
  cursor: pointer;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 20px;
    width: 20px;
    border: 1px solid ${brandColours.primary};
    transition: ${standardTransition('background-color')};
  }

  &:after {
    content: '';
    position: absolute;
    top: 3px;
    left: 7px;
    height: 12px;
    width: 6px;
    border: 2px solid ${standardColours.transparent};
    border-top: none;
    border-left: none;
    border-radius: 1px;
    transform: rotate(45deg);
    transition: ${standardTransition('border-color')};
  }

  &:hover {
    &:after {
      border-color: ${brandColours.primary};
    }
  }
`;

const StyledCheckbox = styled.input`
  ${visuallyHidden()};

  &:checked + ${StyledCheckboxText} {
    &:before {
      background-color: ${brandColours.primary};
    }

    &:after {
      border-color: ${standardColours.white};
    }
  }
`;

const StyledSlider = styled.div`
  padding-top: 20px;
  padding-bottom: 20px;

  .slider-root {
    position: relative;
    display: flex;
    align-items: center;
    user-select: none;
    touch-action: none;
  }

  .slider-track {
    position: relative;
    flex-grow: 1;
    background-color: ${standardColours.grey};
    height: 3px;
    border-radius: 9999px;
  }

  .slider-range {
    position: absolute;
    background-color: ${brandColours.primary};
    height: 100%;
  }

  .slider-thumb {
    position: relative;
    display: block;
    width: 20px;
    height: 20px;
    background-color: ${standardColours.white};
    border: 1px solid ${standardColours.grey};
    box-shadow: 0 2px 5px 0 ${standardColours.lightGrey};
    border-radius: 9999px;
  }

  .slider-thumb:after {
    position: absolute;
    top: 2rem;
    content: '${({ isPrice }) => (isPrice ? '£' : '')}' attr(aria-valuenow);
    ${fontSize(13)}
  }

  .slider-thumb[aria-label='Minimum']:after {
    left: 50%;
    transform: translateX(-50%);
  }

  .slider-thumb[aria-label='Maximum']:after {
    right: 50%;
    transform: translateX(50%);
  }
`;

const StyledItems = styled.div`
  display: grid;
  gap: 20px 15px;
  grid-template-columns: repeat(2, calc(50% - 8px));

  ${minBreakpointQuery.mlarge`
    row-gap: 30px;
  `}

  ${({ showFilters }) => {
    if (showFilters) {
      return css`
        ${minBreakpointQuery.mlarge`
          grid-template-columns: repeat(3, calc(33% - 8px));
        `}

        ${minBreakpointQuery.xlarge`
          grid-template-columns: repeat(4, calc(25% - 8px));
        `}
      `;
    } else {
      return css`
        ${minBreakpointQuery.small`
          grid-template-columns: repeat(3, calc(33% - 8px));
          row-gap: 25px 20px;
        `}

        ${minBreakpointQuery.mlarge`
          grid-template-columns: repeat(4, calc(25% - 12px));
          row-gap: 30px;
        `}

        ${minBreakpointQuery.xlarge`
          grid-template-columns: repeat(5, calc(20% - 12px));
        `}
      `;
    }
  }}

  ${({ isSearch }) => {
    if (!isSearch) {
      return css`
        ${minBreakpointQuery.smedium`
          grid-column: 2 / 3;
        `}
      `;
    }
  }}
`;

const ProductListing = ({
  heading,
  text,
  handle,
  items,
  productBreadcrumbs,
  isSearch,
}) => {
  const algoliaClient = algoliasearch(
    process.env.GATSBY_ALGOLIA_APP_ID,
    process.env.GATSBY_ALGOLIA_SEARCH_KEY,
  );

  const [isFiltered, setFiltered] = useState(false);
  const [displayFilters, setDisplayFilters] = useState(false);
  const [hiddenItems, setHiddenItems] = useState([]);

  const Results = ({ items, showFilters, facetFiltersActive }) => {
    return (
      <StyledItems showFilters={showFilters}>
        {items.map((item, id) => {
          if (facetFiltersActive) {
            // Link directly to product handle
            item.collections = [];
          }
          return (
            <ProductCard
              key={id}
              {...item}
              productBreadcrumbs={productBreadcrumbs}
            />
          );
        })}
      </StyledItems>
    );
  };

  const FilteredResults = () => {
    const { results } = useInstantSearch();

    const facetFiltersActive =
      results.params.includes('facetFilters') ||
      results.params.includes('numericFilters');
    const hits = facetFiltersActive ? results.hits : items;

    return (
      <Results
        items={hits}
        showFilters={true}
        facetFiltersActive={facetFiltersActive}
      />
    );
  };

  const RangeSlider = ({ attribute, isPrice }) => {
    const { start, range, canRefine, refine } = useRange({
      attribute: attribute,
    });
    const { min, max } = range;
    const [value, setValue] = useState([min, max]);

    const from = Math.max(min, Number.isFinite(start[0]) ? start[0] : min);
    const to = Math.min(max, Number.isFinite(start[1]) ? start[1] : max);

    useEffect(() => {
      setValue([from, to]);
    }, [from, to]);

    return (
      <StyledSlider isPrice={isPrice}>
        <Slider.Root
          className="slider-root"
          min={min}
          max={max}
          value={value}
          onValueChange={setValue}
          onValueCommit={e => {
            if (!isFiltered) {
              setFiltered(true);
              setTimeout(() => {
                refine(e);
              }, 200);
            } else {
              refine(e);
            }
          }}
          disabled={!canRefine}
        >
          <Slider.Track className="slider-track">
            <Slider.Range className="slider-range" />
          </Slider.Track>
          <Slider.Thumb className="slider-thumb" />
          <Slider.Thumb className="slider-thumb" />
        </Slider.Root>
      </StyledSlider>
    );
  };

  const RefinementList = ({ attribute, display, isPrice }) => {
    const { items, refine } = useRefinementList({
      attribute: attribute,
    });

    return (
      <StyledFilterList display={display} isPrice={isPrice}>
        {isPrice ? (
          <RangeSlider attribute={attribute} isPrice={isPrice} />
        ) : (
          <StyledFilterListItems>
            {items.map(item => (
              <StyledFilterListItem key={item.label}>
                <label>
                  <StyledCheckbox
                    type="checkbox"
                    checked={item.isRefined}
                    onChange={() => refine(item.value)}
                  />
                  <StyledCheckboxText>
                    {isPrice ? formatPrice(item.label) : item.label}
                  </StyledCheckboxText>
                  <span>({item.count})</span>
                </label>
              </StyledFilterListItem>
            ))}
          </StyledFilterListItems>
        )}
      </StyledFilterList>
    );
  };

  const Filters = () => {
    const { attributesToRender } = useDynamicWidgets();
    const { renderState, results } = useInstantSearch();

    if (
      results.params.includes('facetFilters') ||
      results.params.includes('numericFilters')
    ) {
      setFiltered(true);
    }

    const refinementList =
      renderState['Products'] && renderState['Products'].refinementList;

    const displayedFilters = attributesToRender.filter(
      attribute => refinementList && refinementList[attribute].items.length > 1,
    );

    return attributesToRender.map(attribute => {
      const attributeName =
        attribute === 'priceRangeV2.minVariantPrice.amount'
          ? 'Price'
          : attribute.replace('options.', '');

      return (
        <StyledFilter
          key={attribute}
          display={displayedFilters.includes(attribute)}
          lastDisplayed={displayedFilters.at(-1) === attribute}
        >
          <StyledFilterHeading
            display={!hiddenItems.includes(attribute)}
            onClick={() => {
              if (hiddenItems.includes(attribute)) {
                const index = hiddenItems.indexOf(attribute);
                hiddenItems.splice(index, 1);
                setHiddenItems([...hiddenItems]);
              } else {
                setHiddenItems([...hiddenItems, attribute]);
              }
            }}
          >
            {attributeName}
          </StyledFilterHeading>
          <RefinementList
            attribute={attribute}
            display={hiddenItems.includes(attribute)}
            isPrice={attribute === 'priceRangeV2.minVariantPrice.amount'}
          />
        </StyledFilter>
      );
    });
  };

  return (
    items &&
    items.length > 0 && (
      <StyledProductListing>
        {heading && (
          <StyledHeader>
            <Container narrow={true}>
              <StyledHeading>{heading}</StyledHeading>
              <StyledText content={text} />
            </Container>
          </StyledHeader>
        )}
        <Container wide={true}>
          {!isSearch ? (
            <StyledInner>
              <StyledRefineWrapper>
                <StyledRefine onClick={() => setDisplayFilters(true)}>
                  Filters
                </StyledRefine>
              </StyledRefineWrapper>
              <StyledFilterItems showFilters={items.length > 5}>
                {items.length > 5 && (
                  <InstantSearch
                    indexName="Products"
                    searchClient={algoliaClient}
                    future={{
                      preserveSharedStateOnUnmount: true,
                    }}
                  >
                    <StyledFilters display={displayFilters}>
                      <StyledFiltersHeader>
                        <StyledFiltersHeading>
                          Refine Search
                        </StyledFiltersHeading>
                        <StyledClose onClick={() => setDisplayFilters(false)}>
                          Close
                        </StyledClose>
                      </StyledFiltersHeader>
                      <Filters />
                    </StyledFilters>
                    {isFiltered && <FilteredResults />}
                    <Configure filters={`collections.handle:${handle}`} />
                  </InstantSearch>
                )}
                {!isFiltered && (
                  <Results items={items} showFilters={items.length > 5} />
                )}
              </StyledFilterItems>
            </StyledInner>
          ) : (
            <StyledItems isSearch={true}>
              {items.map((item, id) => {
                item.collections = [];
                return (
                  <ProductCard
                    key={id}
                    {...item}
                    productBreadcrumbs={productBreadcrumbs}
                  />
                );
              })}
            </StyledItems>
          )}
        </Container>
      </StyledProductListing>
    )
  );
};

export default ProductListing;
